import { supabase } from '../supabaseClient';
import { formatPersonName } from './nameUtils';

// ═══════════════════════════════════════════════════════════════════════════
// TB-02 (ADR-013) — Commit Engine.
//
// Executa uma proposed_graph_operation aprovada no banco real.
// Quem chama é responsável por marcar review_status='approved' e gravar
// applied_id/applied_at na proposed_graph_operations.
//
// Regras:
//  - Entity resolution (find_or_create de contato/empresa) acontece aqui.
//  - Campos desconhecidos na tabela-alvo são filtrados (não falham).
//  - Placeholders ($xxx) e IDs ausentes causam skip controlado, não erro.
//  - Duplicatas detectadas por chave natural retornam o id existente.
//  - Vocabulários enum (tipo_socio, propostas.tipo/status, tipo_propriedade)
//    são normalizados para valores válidos dos CHECK constraints reais.
// ═══════════════════════════════════════════════════════════════════════════

const cleanStr = (v) => (v == null ? '' : String(v).trim());
const upper = (v) => cleanStr(v).toUpperCase();
const isPlaceholder = (v) => typeof v === 'string' && v.startsWith('$');

// Vocabulários autoritativos (CHECK constraints conferidos no banco):
const PROPOSTA_TIPO = new Set(['purchase', 'physical_swap', 'financial_swap', 'mixed', 'partnership']);
const PROPOSTA_STATUS = new Set(['draft', 'under_review', 'presented', 'in_negotiation', 'accepted', 'rejected', 'cancelled', 'expired']);
const SOCIO_TIPO = new Set(['partner', 'managing_partner', 'shareholder', 'quotaholder', 'administrator']);
const PROPRIEDADE_TIPO = new Set(['full_ownership', 'bare_ownership', 'usufruct', 'possession', 'judicial']);

const normalizePropostaTipo = (raw) => {
  const v = cleanStr(raw).toLowerCase();
  if (PROPOSTA_TIPO.has(v)) return v;
  if (v === 'permuta' || v === 'swap') return 'physical_swap';
  if (v === 'mista') return 'mixed';
  if (v === 'compra') return 'purchase';
  return 'purchase';
};

const normalizePropostaStatus = (raw) => {
  const v = cleanStr(raw).toLowerCase();
  return PROPOSTA_STATUS.has(v) ? v : 'presented';
};

const normalizeSocioTipo = (raw, ehAdmin) => {
  if (ehAdmin) return 'managing_partner';
  const v = cleanStr(raw).toLowerCase();
  return SOCIO_TIPO.has(v) ? v : 'partner';
};

const normalizePropriedadeTipo = (raw) => {
  const v = cleanStr(raw).toLowerCase();
  return PROPRIEDADE_TIPO.has(v) ? v : null; // null = deixa para o default
};

// Whitelists de campos UPDATE-eáveis por tabela (defesa contra proposed_data
// com chaves "_extraido" ou inventadas).
const KNOWN_UPDATE_FIELDS = {
  empresas: ['company_name', 'empresa', 'cnpj', 'segmento', 'website', 'ramo', 'porte',
    'papel_negocio', 'real_estate_role', 'status_operacional', 'setores', 'notas_estrategicas'],
  imoveis: ['nome', 'matricula', 'cri', 'inscricao_municipal', 'endereco', 'numero',
    'complemento', 'bairro', 'cidade', 'uf', 'cep', 'area_terreno_m2', 'area_construida_m2',
    'tipo_uso_atual', 'situacao', 'valor_venal', 'valor_referencia', 'notas',
    'status_juridico', 'onus_descricao', 'data_ultima_atualizacao_matricula', 'fonte_dados'],
  contatos: ['first_name', 'last_name', 'title', 'email', 'phone', 'mobile', 'cpf', 'observacao'],
};

// Normalização de enums extraídos pelo LLM. O modelo às vezes devolve
// rótulos em PT-BR ("Comercial", "Prédio Comercial", "Livre") em vez do
// valor canônico do CHECK constraint. Mapeia formas conhecidas; valor
// desconhecido é descartado (vira null) para não falhar o UPDATE.
//
// Vocabulários canônicos conferidos via MCP em pg_constraint:
//   imoveis.tipo_uso_atual: residential|commercial|mixed|industrial|vacant|rural|institutional
//   imoveis.situacao:       active|in_negotiation|sold|expropriated|judicial|archived
//   imoveis.status_juridico: sem CHECK, mas o bridge usa livre|com_onus|penhora
const ENUM_NORMALIZE = {
  'imoveis.tipo_uso_atual': {
    canonical: ['residential', 'commercial', 'mixed', 'industrial', 'vacant', 'rural', 'institutional'],
    aliases: {
      residencial: 'residential',
      'prédio residencial': 'residential',
      'predio residencial': 'residential',
      casa: 'residential',
      apartamento: 'residential',
      comercial: 'commercial',
      'prédio comercial': 'commercial',
      'predio comercial': 'commercial',
      'sala comercial': 'commercial',
      escritorio: 'commercial',
      escritório: 'commercial',
      misto: 'mixed',
      'uso misto': 'mixed',
      industrial: 'industrial',
      'galpão industrial': 'industrial',
      'galpao industrial': 'industrial',
      vago: 'vacant',
      vazio: 'vacant',
      terreno: 'vacant',
      'terreno vago': 'vacant',
      rural: 'rural',
      institucional: 'institutional',
    },
  },
  'imoveis.situacao': {
    canonical: ['active', 'in_negotiation', 'sold', 'expropriated', 'judicial', 'archived'],
    aliases: {
      ativo: 'active',
      ativa: 'active',
      'em negociação': 'in_negotiation',
      'em negociacao': 'in_negotiation',
      negociacao: 'in_negotiation',
      vendido: 'sold',
      vendida: 'sold',
      desapropriado: 'expropriated',
      'em juízo': 'judicial',
      'em juizo': 'judicial',
      judicial: 'judicial',
      arquivado: 'archived',
      arquivada: 'archived',
    },
  },
  'imoveis.status_juridico': {
    canonical: ['livre', 'com_onus', 'penhora'],
    aliases: {
      livre: 'livre',
      'sem ônus': 'livre',
      'sem onus': 'livre',
      'sem restrições': 'livre',
      'sem restricoes': 'livre',
      'com ônus': 'com_onus',
      'com onus': 'com_onus',
      onerado: 'com_onus',
      hipotecado: 'com_onus',
      penhorado: 'penhora',
      'em penhora': 'penhora',
    },
  },
};

const normalizeEnum = (table, column, value) => {
  if (value == null) return value;
  const spec = ENUM_NORMALIZE[`${table}.${column}`];
  if (!spec) return value;
  const lower = String(value).toLowerCase().trim();
  if (spec.canonical.includes(lower)) return lower;
  if (spec.aliases[lower] != null) return spec.aliases[lower];
  // Tenta substring para casos como "Prédio Residencial Vertical"
  for (const [alias, canonical] of Object.entries(spec.aliases)) {
    if (lower.includes(alias)) return canonical;
  }
  console.warn(`[commitGraphOperation] valor desconhecido para ${table}.${column}: "${value}" — descartado`);
  return null;
};

const filterKnownFields = (table, data) => {
  const known = KNOWN_UPDATE_FIELDS[table];
  if (!known) return { ...(data || {}) };
  const out = {};
  for (const [k, v] of Object.entries(data || {})) {
    if (!known.includes(k)) continue;
    if (v === undefined || v === null || v === '') continue;
    // Normaliza enums conhecidos antes de aceitar o valor.
    const normalized = ENUM_NORMALIZE[`${table}.${k}`] ? normalizeEnum(table, k, v) : v;
    if (normalized === null || normalized === undefined || normalized === '') continue;
    out[k] = normalized;
  }
  return out;
};

// ── Entity resolution ────────────────────────────────────────────────────

async function findOrCreateContato(nomeRaw, userEmail) {
  const nome = cleanStr(nomeRaw);
  if (!nome) throw new Error('Nome do contato vazio');

  const tokens = nome.split(/\s+/);
  const first = tokens[0];
  const last = tokens.length > 1 ? tokens[tokens.length - 1] : null;
  const nomeUpper = upper(nome);

  // Busca por first_name + last_name (case-insensitive)
  let q = supabase.from('contatos').select('id, first_name, last_name').ilike('first_name', `${first}%`);
  if (last && last !== first) q = q.ilike('last_name', `%${last}%`);
  const { data: candidates } = await q.limit(10);

  if (candidates && candidates.length > 0) {
    const exact = candidates.find((c) => {
      const full = upper(`${c.first_name || ''} ${c.last_name || ''}`.trim());
      return full === nomeUpper;
    });
    if (exact) return exact.id;
    return candidates[0].id;
  }

  // Não achou — cria como tipo_contato='socio' (default mais comum no contexto TB-02).
  // 06/mai/26: nome em Title Case (ADR-001 PF).
  const { data: created, error } = await supabase
    .from('contatos')
    .insert({
      first_name: formatPersonName(first),
      last_name: last && last !== first ? formatPersonName(tokens.slice(1).join(' ')) : null,
      tipo_contato: 'socio',
      updated_by: userEmail,
    })
    .select('id')
    .single();
  if (error) throw new Error(`Criar contato "${nome}": ${error.message}`);
  return created.id;
}

async function findOrCreateEmpresa(nomeRaw, userEmail) {
  const nome = cleanStr(nomeRaw);
  if (!nome) throw new Error('Nome da empresa vazio');
  const nomeUpper = upper(nome);
  const firstToken = nomeUpper.split(/\s+/)[0];
  if (!firstToken) throw new Error('Token vazio');

  const { data } = await supabase
    .from('empresas')
    .select('id, company_name')
    .ilike('company_name', `%${firstToken}%`)
    .limit(10);

  if (data && data.length > 0) {
    const exact = data.find((e) => upper(e.company_name) === nomeUpper);
    if (exact) return exact.id;
    const contains = data.find((e) => {
      const c = upper(e.company_name);
      return c.includes(nomeUpper) || nomeUpper.includes(c);
    });
    if (contains) return contains.id;
  }

  const { data: created, error } = await supabase
    .from('empresas')
    .insert({ company_name: nomeUpper, updated_by: userEmail })
    .select('id')
    .single();
  if (error) throw new Error(`Criar empresa "${nomeUpper}": ${error.message}`);
  return created.id;
}

// ── Commits por tipo de operação ─────────────────────────────────────────

async function commitCreateProposal(op, userEmail) {
  const d = op.proposed_data || {};
  if (!d.oferta_id || isPlaceholder(d.oferta_id)) {
    return { success: false, error: 'oferta_id ausente ou é placeholder — resolva oferta primeiro', skip: true };
  }
  if (!d.nome) return { success: false, error: 'nome obrigatório' };

  const row = {
    oferta_id: d.oferta_id,
    nome: d.nome,
    tipo: normalizePropostaTipo(d.tipo),
    status: normalizePropostaStatus(d.status),
    valor_proposto: d.valor_proposto ?? null,
    valor_m2: d.valor_m2 ?? null,
    condicoes_pagamento: d.condicoes_pagamento || null,
    percentual_permuta: d.percentual_permuta ?? null,
    data_proposta: d.data_proposta || null,
    proponente_empresa_id: d.proponente_empresa_id || null,
    updated_by: userEmail,
  };

  const { data, error } = await supabase.from('propostas').insert(row).select('id, nome').single();
  if (error) return { success: false, error: error.message };
  return { success: true, applied_id: data.id, message: `Proposta "${data.nome}" criada` };
}

async function commitUpdateEntity(op, userEmail) {
  const targetId = op.target_id || op.resolved_to || op.match_candidates?.[0]?.id;
  if (!targetId) return { success: false, error: 'Sem target_id para update' };

  const safe = filterKnownFields(op.target_table, op.proposed_data);
  if (Object.keys(safe).length === 0) {
    return { success: false, error: `Nenhum campo whitelisted em ${op.target_table}`, skip: true };
  }

  // updated_by/updated_at apenas em tabelas que têm essas colunas
  const knownCols = KNOWN_UPDATE_FIELDS[op.target_table];
  if (knownCols) {
    safe.updated_by = userEmail;
    safe.updated_at = new Date().toISOString();
  }

  const { error } = await supabase.from(op.target_table).update(safe).eq('id', targetId);
  if (error) return { success: false, error: error.message };

  const fieldsList = Object.keys(safe).filter((k) => k !== 'updated_by' && k !== 'updated_at').join(', ');
  return { success: true, applied_id: targetId, message: `${op.target_table} atualizado (${fieldsList})` };
}

async function commitSocio(d, userEmail) {
  if (!d.empresa_id || isPlaceholder(d.empresa_id)) {
    return { success: false, error: 'empresa_id ausente ou é placeholder', skip: true };
  }

  // proposed_data pode vir com socio_empresa_id (PJ) já resolvido ou com
  // socio_nome_extraido para resolver. Suporta ambos.
  let socioContatoId = d.socio_contato_id || null;
  let socioEmpresaId = d.socio_empresa_id || null;

  if (!socioContatoId && !socioEmpresaId) {
    const nome = d.socio_nome_extraido || d.nome || d.proprietario_nome;
    if (!nome) return { success: false, error: 'nome do sócio ausente' };
    socioContatoId = await findOrCreateContato(nome, userEmail);
  }

  // CHECK chk_socio_tipo: XOR entre contato/empresa
  if (socioContatoId && socioEmpresaId) socioEmpresaId = null;

  const row = {
    empresa_id: d.empresa_id,
    socio_contato_id: socioContatoId,
    socio_empresa_id: socioEmpresaId,
    percentual_participacao: d.percentual_participacao ?? d.percentual ?? null,
    tipo_socio: normalizeSocioTipo(d.tipo_socio, d.eh_administrador),
    is_active: true,
    updated_by: userEmail,
  };

  // Idempotência: mesmo (empresa_id, socio_*) já existente
  let dupQ = supabase.from('socios').select('id').eq('empresa_id', row.empresa_id);
  if (socioContatoId) dupQ = dupQ.eq('socio_contato_id', socioContatoId);
  else dupQ = dupQ.eq('socio_empresa_id', socioEmpresaId);
  const { data: existing } = await dupQ.limit(1);
  if (existing && existing.length > 0) {
    return { success: true, applied_id: existing[0].id, message: 'Sócio já vinculado' };
  }

  const { data, error } = await supabase.from('socios').insert(row).select('id').single();
  if (error) return { success: false, error: error.message };
  return { success: true, applied_id: data.id, message: 'Sócio vinculado' };
}

async function commitProprietario(d, userEmail) {
  if (!d.imovel_id || isPlaceholder(d.imovel_id)) {
    return { success: false, error: 'imovel_id ausente ou é placeholder', skip: true };
  }

  let propEmpresaId = d.proprietario_empresa_id || null;
  let propContatoId = d.proprietario_contato_id || null;

  if (!propEmpresaId && !propContatoId) {
    const nome = d.proprietario_nome_extraido || d.proprietario_nome || d.nome;
    if (!nome) return { success: false, error: 'nome do proprietário ausente' };
    const isPJ = d.proprietario_type === 'PJ' || d.tipo_pessoa === 'pj' || /LTDA|S\.?A\.?|EIRELI|MEI|PARTICIPAÇÕES|PARTICIPACOES/i.test(nome);
    if (isPJ) propEmpresaId = await findOrCreateEmpresa(nome, userEmail);
    else propContatoId = await findOrCreateContato(nome, userEmail);
  }

  // CHECK chk_proprietario_tipo: XOR
  if (propEmpresaId && propContatoId) propContatoId = null;

  const pct = d.percentual_participacao ?? d.percentual ?? 100;
  if (pct <= 0 || pct > 100) {
    return { success: false, error: `percentual_participacao inválido: ${pct}` };
  }

  const row = {
    imovel_id: d.imovel_id,
    proprietario_empresa_id: propEmpresaId,
    proprietario_contato_id: propContatoId,
    percentual_participacao: pct,
    tipo_propriedade: normalizePropriedadeTipo(d.tipo_propriedade) || 'full_ownership',
    data_aquisicao: d.data_aquisicao || null,
    updated_by: userEmail,
  };

  // Idempotência
  let dupQ = supabase.from('imovel_proprietarios').select('id').eq('imovel_id', row.imovel_id);
  if (propEmpresaId) dupQ = dupQ.eq('proprietario_empresa_id', propEmpresaId);
  else dupQ = dupQ.eq('proprietario_contato_id', propContatoId);
  const { data: existing } = await dupQ.limit(1);
  if (existing && existing.length > 0) {
    return { success: true, applied_id: existing[0].id, message: 'Proprietário já vinculado' };
  }

  const { data, error } = await supabase.from('imovel_proprietarios').insert(row).select('id').single();
  if (error) return { success: false, error: error.message };
  return { success: true, applied_id: data.id, message: 'Proprietário vinculado' };
}

async function commitPropostaImovel(d) {
  if (!d.proposta_id || isPlaceholder(d.proposta_id)) {
    return { success: false, error: 'proposta_id é placeholder — aprove a proposta primeiro', skip: true };
  }
  if (!d.imovel_id || isPlaceholder(d.imovel_id)) {
    return { success: false, error: 'imovel_id é placeholder', skip: true };
  }

  const { data: existing } = await supabase
    .from('proposta_imoveis')
    .select('id')
    .eq('proposta_id', d.proposta_id)
    .eq('imovel_id', d.imovel_id)
    .limit(1);
  if (existing && existing.length > 0) {
    return { success: true, applied_id: existing[0].id, message: 'Vínculo já existia' };
  }

  const row = {
    proposta_id: d.proposta_id,
    imovel_id: d.imovel_id,
    valor_especifico: d.valor_especifico ?? null,
    area_especifica_m2: d.area_especifica_m2 ?? null,
  };
  const { data, error } = await supabase.from('proposta_imoveis').insert(row).select('id').single();
  if (error) return { success: false, error: error.message };
  return { success: true, applied_id: data.id, message: 'Imóvel vinculado à proposta' };
}

async function commitCreateEdge(op, userEmail) {
  const d = op.proposed_data || {};
  switch (op.target_table) {
    case 'socios': return commitSocio(d, userEmail);
    case 'imovel_proprietarios': return commitProprietario(d, userEmail);
    case 'proposta_imoveis': return commitPropostaImovel(d);
    default: return { success: false, error: `Junction desconhecida: ${op.target_table}` };
  }
}

async function commitLinkDocument(op, userEmail) {
  const d = op.proposed_data || {};
  if (!d.entity_id || isPlaceholder(d.entity_id)) {
    return { success: false, error: 'entity_id é placeholder — aprove a entidade primeiro', skip: true };
  }
  if (!d.entity_type) return { success: false, error: 'entity_type ausente' };

  const attachmentId = d.attachment_id || op.source_document_id;
  if (!attachmentId) return { success: false, error: 'attachment_id ausente' };

  const { data: existing } = await supabase
    .from('document_links')
    .select('id')
    .eq('attachment_id', attachmentId)
    .eq('entity_type', d.entity_type)
    .eq('entity_id', d.entity_id)
    .limit(1);
  if (existing && existing.length > 0) {
    return { success: true, applied_id: existing[0].id, message: 'Vínculo já existia' };
  }

  const { data, error } = await supabase
    .from('document_links')
    .insert({
      attachment_id: attachmentId,
      entity_type: d.entity_type,
      entity_id: d.entity_id,
      relation_type: d.relation_type || 'source_document',
      created_by: userEmail,
    })
    .select('id')
    .single();
  if (error) return { success: false, error: error.message };
  return { success: true, applied_id: data.id, message: `Documento vinculado a ${d.entity_type}` };
}

// ── Dispatcher principal ─────────────────────────────────────────────────

export async function commitGraphOperation(op, userEmail) {
  if (!op || !op.operation) return { success: false, error: 'operação inválida' };
  try {
    switch (op.operation) {
      case 'create_proposal': return await commitCreateProposal(op, userEmail);
      case 'create_entity':
      case 'update_entity':   return await commitUpdateEntity(op, userEmail);
      case 'create_edge':     return await commitCreateEdge(op, userEmail);
      case 'update_edge':     return { success: false, error: 'update_edge ainda não suportado', skip: true };
      case 'link_document':   return await commitLinkDocument(op, userEmail);
      default:                return { success: false, error: `Operação desconhecida: ${op.operation}` };
    }
  } catch (err) {
    console.error('[commitGraphOperation] falhou:', err);
    return { success: false, error: err.message || String(err) };
  }
}

// Ordem de aprovação ao processar lote: criar entidades antes de vincular documentos.
export const APPROVE_ORDER = {
  create_proposal: 0,
  create_entity: 1,
  update_entity: 2,
  create_edge: 3,
  link_document: 4,
  update_edge: 5,
};
